import { endpoints, oids } from "@/configs/Settings";
import { HTTPResponseStatus } from "@/utils/enum";
import { useAuth } from "@/utils/useAuth";
import { useAxios } from "@/utils/useAxios";

interface GetMedicationRequestByOrganizationParams {
  /** Organization id sem prefixo, ex: 05490544000100 */
  organizationId: string;
  /** Status (ex: active, completed) */
  status?: string | string[];
  /** Data inicial YYYY-MM-DD (opcional) */
  startDate?: string;
  /** Data final YYYY-MM-DD (opcional) */
  endDate?: string;
  /** Quantidade de registros (default 100) */
  count?: number;
  /** Incluir Encounter vinculado */
  includeEncounter?: boolean;
  /** Incluir Patient no bundle */
  includePatient?: boolean;
  /** Indica se deve retornar apenas o resumo (opcional) */
  summary?: boolean;
}

/**
 * Busca MedicationRequest por organização via encounter.service-provider
 */
export async function GetMedicationRequestByOrganization({
  organizationId,
  status,
  startDate,
  endDate,
  count = 100,
  includeEncounter,
  includePatient,
  summary,
}: GetMedicationRequestByOrganizationParams) {
  let url = `${endpoints.STATISTICS}/MedicationRequest?encounter.service-provider=Organization/${oids.cnpj}-${organizationId}`;
  if (status) {
    const value = Array.isArray(status) ? status.join(",") : status;
    url = `${url}&status=${encodeURIComponent(value)}`;
  }
  if (startDate) url = `${url}&_lastUpdated=ge${startDate}`;
  if (endDate) url = `${url}&_lastUpdated=le${endDate}`;
  if (count) url = `${url}&_count=${count}`;
  if (includeEncounter) url = `${url}&_include=MedicationRequest:encounter`;
  if (includePatient) url = `${url}&_include=MedicationRequest:patient`;
  if (summary) url = `${url}&_summary=count`;

  const subjectId = await useAuth.getSubjectId();
  const orgIdHeader = await useAuth.getOrganizationId();

  const headers = {
    Accept: "application/fhir+json",
    Authorization: await useAuth.getUserToken(),
    "Content-Type": "application/fhir+json",
    ...(subjectId && { "subject-id": subjectId }),
    ...(orgIdHeader && { "organization-id": orgIdHeader }),
  } as Record<string, string>;

  const response = await useAxios
    .get(url, { headers, timeout: 20000 })
    .then((response) => {
      if (response.status !== HTTPResponseStatus.Success) {
        throw new Error(
          `Get MedicationRequest By Organization: ${response.status}`
        );
      }
      return response.data as fhir5.Bundle;
    })
    .catch((error) => {
      console.error("GetMedicationRequestByOrganization ", error);
      throw new Error();
    });

  return response;
}
